import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { capitalize, isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useFragment } from "react-relay";

import { lessonTypeHasRateForMembershipType } from "@/screens/Lessons/helpers/lessonTypeHasRateForMembershipType";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useFacilityInstructorNoun } from "@whoosh/features/src/Facility";

import { useFacilityLessonBookingDays } from "../../../../../../../features/Facility/hooks/useFacilityLessonBookingDays";
import { LessonsStackNavigatorParamList } from "../../../../../../../navigators/LessonsStackNavigator/types";
import { InstructorLessonTypeSelect } from "../../../../InstructorDetail/components/InstructorLessonTypeSelect";
import { InstructorPhoto } from "../../../../InstructorDetail/components/InstructorPhoto";
import { lessonTypeFilterAtom } from "../../../atoms";
import { AvailableDatesList } from "./AvailableDatesList";
import { InstructorCardFragment_facility$key } from "./__generated__/InstructorCardFragment_facility.graphql";
import { InstructorCardFragment_session$key } from "./__generated__/InstructorCardFragment_session.graphql";
import { InstructorCardFragment_staffProfile$key } from "./__generated__/InstructorCardFragment_staffProfile.graphql";

type Props = {
  instructorRef: InstructorCardFragment_staffProfile$key | null;
  sessionRef: InstructorCardFragment_session$key | null;
  facilityRef: InstructorCardFragment_facility$key | null;
};
export const InstructorCard = ({
  facilityRef,
  instructorRef,
  sessionRef,
}: Props) => {
  const navigation =
    useNavigation<NavigationProp<LessonsStackNavigatorParamList>>();

  const isMobile = useResponsiveProp({ base: 1, lg: 0 });
  const lessonTypeFilter = useAtomValue(lessonTypeFilterAtom);

  const route =
    useRoute<RouteProp<LessonsStackNavigatorParamList, "LessonsHome">>();

  const facilitySlug = route?.params?.facilitySlug;

  const instructor = useFragment(InstructorCardFragmentString, instructorRef);
  const session = useFragment(InstructorCardSessionFragmentString, sessionRef);
  const facility = useFragment(
    InstructorCardFacilityFragmentString,
    facilityRef
  );

  const currentMembershipTypeId =
    session?.currentClientProfile?.member?.membershipType?.id || "";
  const instructorId = instructor?.id;
  const facilityId = facility?.id;

  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [lessonTypeId, setLessonTypeId] = useState<string | undefined>(
    undefined
  );

  const onChange = (lessonTypeId: string, duration: number) => {
    setDuration(duration);
    setLessonTypeId(lessonTypeId);
  };

  useEffect(() => {
    if (lessonTypeFilter) {
      if (lessonTypeFilter.id) {
        setLessonTypeId(lessonTypeFilter.id);
        setDuration(lessonTypeFilter.defaultDuration);
      }
    } else {
      setLessonTypeId(undefined);
      setDuration(undefined);
    }
  }, [lessonTypeFilter]);

  const instructorLessonTypeIds =
    instructor?.lessonTypes?.edges?.flatMap((e) =>
      e?.node?.id &&
      e?.node.visibleToMembers &&
      !e?.node?.archivedAt &&
      lessonTypeHasRateForMembershipType(e.node, currentMembershipTypeId)
        ? [e.node.id]
        : []
    ) ?? [];

  const hasAFilteredLessonType = lessonTypeFilter
    ? instructorLessonTypeIds.includes(lessonTypeFilter.id)
    : true;

  const bookingWindowDays = useFacilityLessonBookingDays(facility ?? null);

  const instructorNoun = useFacilityInstructorNoun(facility ?? null);

  const onPress = useCallback(() => {
    if (instructorId) {
      navigation.navigate("InstructorDetail", {
        facilitySlug,
        instructorId,
      });
    }
  }, [facilitySlug, instructorId, navigation]);

  return instructorId && facilityId ? (
    <VStack gap={4} testID={`instructor-card-${instructorId}`}>
      <HStack gap={{ base: 4, lg: 6 }} px={6}>
        <Pressable onPress={onPress}>
          <InstructorPhoto
            instructorRef={instructor}
            size={isMobile ? "lg" : "xl"}
          />
        </Pressable>
        <VStack alignSelf="center" gap={1} flex={1}>
          <Pressable onPress={onPress} alignSelf="flex-start">
            <Text variant="mainHeading">{instructor?.user.fullName}</Text>
          </Pressable>
          <Text color="text-secondary">
            {instructor?.instructorDepartmentProfile.headline}
          </Text>
          {!isMobile && hasAFilteredLessonType ? (
            <Box pt={4}>
              <InstructorLessonTypeSelect
                sessionRef={session ?? null}
                onChange={onChange}
                duration={duration}
                lessonTypeId={lessonTypeId}
                facilitySlug={facilitySlug}
                instructorId={instructorId}
                testID={`instructor-lesson-type-select-${instructorId}`}
              />
            </Box>
          ) : null}
          {!isMobile && !hasAFilteredLessonType ? (
            <Box pt={4}>
              <DoesNotSupportLessonType
                instructorTerm={instructorNoun.singular}
              />
            </Box>
          ) : null}
        </VStack>
      </HStack>
      {isMobile && hasAFilteredLessonType ? (
        <Box px={6}>
          <InstructorLessonTypeSelect
            sessionRef={session ?? null}
            onChange={onChange}
            duration={duration}
            lessonTypeId={lessonTypeId}
            facilitySlug={facilitySlug}
            instructorId={instructorId}
            testID={`instructor-lesson-type-select-${instructorId}`}
          />
        </Box>
      ) : null}
      {isMobile && !hasAFilteredLessonType ? (
        <Box px={6}>
          <DoesNotSupportLessonType instructorTerm={instructorNoun.singular} />
        </Box>
      ) : null}
      {hasAFilteredLessonType ? (
        <Box px={7}>
          <Text variant="mainTiny">{t`NEXT AVAILABILITY`}</Text>
        </Box>
      ) : null}
      {!isNil(duration) && hasAFilteredLessonType ? (
        <AvailableDatesList
          instructorId={instructorId}
          facilityId={facilityId}
          durationMins={duration}
          lessonTypeId={lessonTypeId}
          bookingWindowDays={bookingWindowDays}
        />
      ) : null}
    </VStack>
  ) : null;
};

const DoesNotSupportLessonType = ({
  instructorTerm,
}: {
  instructorTerm: string;
}) => {
  return (
    <Box
      px={6}
      borderColor="surface-dividers"
      borderWidth={2}
      height={48}
      borderRadius="2"
      justifyContent="center"
      alignItems="center"
    >
      <Text variant="mainSmall" color="text-secondary">{t`${capitalize(
        instructorTerm
      )} does not support lesson type`}</Text>
    </Box>
  );
};

const InstructorCardFragmentString = graphql`
  fragment InstructorCardFragment_staffProfile on StaffProfile {
    id
    ...InstructorPhotoFragment_staffProfile
    instructorDepartmentProfile(facilitySlug: $facilitySlug) {
      headline
    }
    lessonTypes(facilitySlug: $facilitySlug) {
      edges {
        node {
          id
          archivedAt
          visibleToMembers
          ...lessonTypeHasRateForMembershipType_lessonType
        }
      }
    }
    user {
      fullName
    }
  }
`;

const InstructorCardSessionFragmentString = graphql`
  fragment InstructorCardFragment_session on Session {
    ...InstructorLessonTypeSelect_session
    currentClientProfile {
      member {
        membershipType {
          id
        }
      }
    }
  }
`;

const InstructorCardFacilityFragmentString = graphql`
  fragment InstructorCardFragment_facility on Facility {
    id
    ...useFacilityLessonBookingDays_facility
    ...useFacilityInstructorNoun_facility
  }
`;
